import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { NavigationEnd, RouterOutlet } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, SilentRequest } from '@azure/msal-browser';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { SweetAlertService } from './Shared/services/sweet-alert.service';
import { AgencyServiceService } from './Core/services/agency.service';
import { UserService } from './Shared/services/user.service';
import { LanguageService } from './Shared/services/language.service';
import { Router } from '@angular/router';
import { PlaneSpinnerComponent } from './Shared/Components/Utilities-Components/plane-spinner/plane-spinner.component';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { TokenService } from './Core/services/token.service';
import { Location } from '@angular/common';
import { filter } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  standalone: true,
  imports: [RouterOutlet, PlaneSpinnerComponent, NgxSpinnerComponent],
})
export class AppComponent {
  tokenService = inject(TokenService);

  @ViewChild('loadagencyinfo') loadAgencyInfo!: ElementRef;
  @ViewChild('loadagencyinfoicon') loadagencyinfoicon!: ElementRef;

  azureAccessToken: string | null = '';

  previousUrl: string | null = null;
  currentUrl: string | null = null;

  constructor(
    private translate: TranslateService,
    private langService: LanguageService,
    private router: Router,
    private msalService: MsalService,
    private sweetAlert: SweetAlertService,
    private agencyService: AgencyServiceService,
    private userService: UserService,
    private location: Location,
  ) {
    this.azureAccessToken = localStorage.getItem('azureAccessToken');
    this.translate.addLangs(this.langService.supportedLangs);
    this.translate.setDefaultLang(this.langService.defaultLang);
  }

  ngOnInit() {
    // In local dev, clear stale cloud userdata immediately so child pages
    // don't call the local BE with the cloud agencyId before the refresh completes.
    if (this.azureAccessToken && environment.agencyUrl.includes('localhost')) {
      this.userService.clearUser();
    }
    this.initializeMsal();

    const stored = localStorage.getItem('Language');
    if (!stored) {
      localStorage.setItem('Language', this.langService.getBrowserLang());
    }
    this.translate.use(this.langService.resolveLang());

    // After every navigation (including browser back/forward), make sure the
    // visible URL bar carries the current language prefix. This prevents a
    // popstate from quietly resurrecting an old `/en/...` URL while the user
    // has already switched to e.g. `/ar/...`.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.ensureLangInUrl());
  }

  private ensureLangInUrl() {
    const lang = this.langService.resolveLang();
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);
    const first = segments[0]?.toLowerCase();

    if (first === lang) return;

    const stripped = this.langService.stripLang(path);
    const newPath = `/${lang}${stripped === '/' ? '' : stripped}`;
    const newUrl = newPath + window.location.search + window.location.hash;
    this.location.replaceState(newUrl);
  }

  ngAfterViewInit(): void {
    if (!this.azureAccessToken) {
      document.body.style.overflow = 'hidden';
      let loadAgencyInfo = this.loadAgencyInfo.nativeElement as HTMLDivElement;
      loadAgencyInfo.classList.remove('d-none');
    }
  }

  initializeMsal() {
    this.msalService.initialize().subscribe({
      next: () => {
        if (!this.azureAccessToken) {
          this.handleRedirectResponse();
        } else if (!this.userService.hasUserData()) {
          // Token present but no agency data: happens after a login redirect
          // whose `agents/data` call failed (the app would otherwise run
          // "half logged in": no name/logo/balance and `role: ''` in the
          // booking body, rejected by the BE). In local dev this is also the
          // normal path: userdata is cleared in ngOnInit so the agencyId in
          // localStorage reflects the local DB seed (not the cloud UUID).
          this.recoverUserData();
        }
      },
      error(err) {},
    });
  }

  /**
   * Ricarica `userdata` da `agents/data`. Se non è possibile, l'app non deve
   * restare usabile a metà: si riparte da un login pulito (in locale l'errore
   * viene ignorato come prima, per non rimbalzare sul B2C con il mock BE).
   */
  recoverUserData() {
    this.agencyService.getAgencyByAccessToken().subscribe({
      next: (res) => this.userService.setUser(res),
      error: () => {
        if (environment.agencyUrl.includes('localhost')) return;

        this.sweetAlert.customWithActions(
          this.translate.instant('Unable to load your agency data'),
          this.translate.instant(
            'Your agency data could not be loaded. Please sign in again to continue.',
          ),
          '/assets/icons/refreshing.svg',
          this.translate.instant('Sign in'),
          () => this.tokenService.login(),
        );
      },
    });
  }

  handleRedirectResponse() {
    setTimeout(() => {
      const azureKeys = localStorage.getItem('msal.account.keys');

      if (!azureKeys) this.tokenService.login();
    }, 2000);
    this.msalService.instance
      .handleRedirectPromise()
      .then((res: AuthenticationResult | null) => {
        if (res !== null) {
          this.msalService.instance.setActiveAccount(res.account);

          localStorage.setItem(
            'azureId',
            res?.account?.idTokenClaims?.sub || '',
          );

          localStorage.setItem(
            'azureEmail',
            res?.account?.idTokenClaims?.emails?.[0] || '',
          );
          localStorage.setItem('azureUserName', res?.account?.username || '');
          localStorage.setItem('azureIdToken', res?.idToken);

          this.getAccessToken(res.account?.homeAccountId);
        }
      })
      .catch((error) => {
        this.sweetAlert.sweetError('Redirect Error', error);
      });
  }

  getAccessToken(accountId: string | undefined) {
    if (!accountId) {
      this.sweetAlert.sweetError(
        'Error',
        'No account found. Please log in first.',
      );

      return;
    }

    let scope = environment.scope;

    const request: SilentRequest = {
      scopes: [scope],
      account:
        this.msalService.instance.getAccountByHomeId(accountId) || undefined,
    };

    this.msalService.instance
      .acquireTokenSilent(request)
      .then((res) => {
        localStorage.setItem('azureAccessToken', res.accessToken);

        this.getAgencyInfo();
      })
      .catch((error) => {
        this.sweetAlert.sweetError('Token Acquisition Error', error);
      });
  }

  getAgencyInfo() {
    this.agencyService.getAgencyByAccessToken().subscribe({
      next: (res) => {
        this.userService.setUser(res);

        localStorage.setItem('login', 'true');

        this.handelGetAgencyRequest();

        this.router.navigate(['/dashboard']);
      },
      error: (err) => {
        this.handelGetAgencyRequest(err);
      },
    });
  }

  handelGetAgencyRequest(err?: any) {
    let loadAgencyInfo = this.loadAgencyInfo.nativeElement as HTMLDivElement;
    let loadAgencyInfoIcon = this.loadagencyinfoicon
      .nativeElement as HTMLDivElement;

    loadAgencyInfoIcon.classList.add('departure');
    setTimeout(() => {
      loadAgencyInfo.classList.add('d-none');
      document.body.style.overflow = 'unset';
      if (err) this.sweetAlert.sweetErrors('Oops', err);
    }, 1800);
  }
}
